import React, { useState, Children } from 'react';
import { TargetIcon, FilterIcon, RocketIcon, MessageSquareIcon, ClipboardCheckIcon, HandshakeIcon, BookOpenIcon, UsersIcon, GraduationCapIcon, ShieldCheckIcon, ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function InteractivePipeline() {
  const [activeStage, setActiveStage] = useState<number | null>(null);
  const stages = [{
    icon: TargetIcon,
    title: 'Attract',
    description: 'Targeted messaging and reach bring the right candidates into the ecosystem.'
  }, {
    icon: MessageSquareIcon,
    title: 'Engage',
    description: 'Initial connection creates interest, alignment, and forward momentum.'
  }, {
    icon: FilterIcon,
    title: 'Screen',
    description: 'Baseline fit, communication ability, and intent are quickly evaluated.'
  }, {
    icon: ClipboardCheckIcon,
    title: 'Qualify',
    description: 'Deeper alignment with the role, expectations, and performance potential is confirmed.'
  }, {
    icon: HandshakeIcon,
    title: 'Commit',
    description: 'High-intent candidates make a clear, personal decision to move forward.'
  }, {
    icon: BookOpenIcon,
    title: 'Onboard',
    description: 'Structure, expectations, and systems are introduced from day one.'
  }, {
    icon: UsersIcon,
    title: 'Integrate',
    description: 'New reps connect to the culture, team, and workflows.'
  }, {
    icon: GraduationCapIcon,
    title: 'Train',
    description: 'Skills and confidence develop through repetition and guided reinforcement.'
  }, {
    icon: RocketIcon,
    title: 'Launch',
    description: 'Reps are prepared to enter active production.'
  }, {
    icon: ShieldCheckIcon,
    title: 'Retain',
    description: 'Consistency, growth, and long-term engagement are reinforced.'
  }];
  const toggleStage = (index: number) => {
    setActiveStage(activeStage === index ? null : index);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section className="section-padding bg-white" data-id="element-686">
      <div className="container-custom" data-id="element-687">
        <motion.div className="max-w-3xl mx-auto text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.5
      }} data-id="element-688">
          <h2 className="mb-6" data-id="element-689">The 10 Essential Recruiting Stages</h2>
          <p className="text-xl text-gray-600 leading-relaxed" data-id="element-690">
            Each rep moves through a structured, intentional process. This isn't
            a "funnel" — it's an operational pipeline designed to improve
            consistency, commitment, and performance.
          </p>
        </motion.div>

        {/* Desktop Horizontal Pipeline */}
        <div className="hidden lg:block max-w-7xl mx-auto" data-id="element-691">
          {/* Stage Buttons */}
          <motion.div className="flex justify-between gap-2 mb-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-692">
            {stages.map((stage, index) => {
            const Icon = stage.icon;
            const isActive = activeStage === index;
            return <motion.button key={index} onClick={() => toggleStage(index)} className={`flex-1 group transition-all duration-300 ${isActive ? 'transform scale-105' : 'hover:transform hover:scale-105'}`} variants={itemVariants} data-id="element-693">
                  <div className={`rounded-xl p-4 border-2 transition-all duration-300 ${isActive ? 'bg-slate border-slate shadow-lg' : 'bg-white border-gray-200 hover:border-slate'}`} data-id="element-694">
                    <div className={`w-12 h-12 mx-auto mb-2 rounded-lg flex items-center justify-center transition-colors ${isActive ? 'bg-white' : 'bg-deep-blue'}`} data-id="element-695">
                      <Icon className={`h-6 w-6 transition-colors ${isActive ? 'text-slate' : 'text-slate'}`} data-id="element-696" />
                    </div>
                    <div className={`text-xs font-bold mb-1 ${isActive ? 'text-white' : 'text-gray-600'}`} data-id="element-697">
                      {index + 1}
                    </div>
                    <div className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-deep-blue'}`} data-id="element-698">
                      {stage.title}
                    </div>
                    <motion.div animate={{
                  rotate: isActive ? 180 : 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-699">
                      <ChevronDownIcon className={`h-4 w-4 mx-auto mt-2 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-slate'}`} data-id="element-700" />
                    </motion.div>
                  </div>
                </motion.button>;
          })}
          </motion.div>

          {/* Expanded Description */}
          <div className="min-h-[120px]" data-id="element-701">
            <AnimatePresence mode="wait" data-id="element-702">
              {activeStage !== null && <motion.div key={activeStage} initial={{
              opacity: 0,
              y: -10,
              height: 0
            }} animate={{
              opacity: 1,
              y: 0,
              height: 'auto'
            }} exit={{
              opacity: 0,
              y: -10,
              height: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeOut'
            }} className="overflow-hidden" data-id="element-703">
                  <div className="bg-gray-50 rounded-xl p-8 border-2 border-slate" data-id="element-704">
                    <div className="max-w-3xl mx-auto text-center" data-id="element-705">
                      <h3 className="text-2xl mb-4 text-deep-blue" data-id="element-706">
                        Stage {activeStage + 1}: {stages[activeStage].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed" data-id="element-707">
                        {stages[activeStage].description}
                      </p>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile Accordion */}
        <div className="lg:hidden max-w-2xl mx-auto space-y-3" data-id="element-708">
          {stages.map((stage, index) => {
          const Icon = stage.icon;
          const isActive = activeStage === index;
          return <motion.button key={index} onClick={() => toggleStage(index)} className="w-full text-left" initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} data-id="element-709">
                <div className={`rounded-xl p-4 border-2 transition-all ${isActive ? 'bg-slate border-slate' : 'bg-white border-gray-200'}`} data-id="element-710">
                  <div className="flex items-center justify-between" data-id="element-711">
                    <div className="flex items-center gap-4" data-id="element-712">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-white' : 'bg-deep-blue'}`} data-id="element-713">
                        <Icon className={`h-6 w-6 ${isActive ? 'text-slate' : 'text-slate'}`} data-id="element-714" />
                      </div>
                      <div data-id="element-715">
                        <div className={`text-xs font-bold mb-1 ${isActive ? 'text-white' : 'text-gray-600'}`} data-id="element-716">
                          Stage {index + 1}
                        </div>
                        <div className={`text-lg font-semibold ${isActive ? 'text-white' : 'text-deep-blue'}`} data-id="element-717">
                          {stage.title}
                        </div>
                      </div>
                    </div>
                    <motion.div animate={{
                  rotate: isActive ? 180 : 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-718">
                      <ChevronDownIcon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} data-id="element-719" />
                    </motion.div>
                  </div>
                  <AnimatePresence data-id="element-720">
                    {isActive && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-721">
                        <div className="mt-4 pt-4 border-t border-white/20" data-id="element-722">
                          <p className="text-white leading-relaxed" data-id="element-723">
                            {stage.description}
                          </p>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </motion.button>;
        })}
        </div>

        <motion.div className="mt-12 text-center" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.5
      }} data-id="element-724">
          <div className="inline-block bg-deep-blue/5 rounded-xl p-6 max-w-2xl border border-deep-blue/10" data-id="element-725">
            <p className="text-gray-700 leading-relaxed" data-id="element-726">
              <span className="font-bold text-deep-blue" data-id="element-727">
                This is not a one-time journey.
              </span>{' '}
              It's a continuous loop that keeps delivering qualified candidates
              while supporting the development of your active team.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}